import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getAvatarStyle } from '../../lib/s3';

/**
 * AvatarDisplay - Shared component for displaying avatars with themed backgrounds
 * 
 * Supports both legacy avatars (rj-1-rectangle.png) and new-style avatars (avatar_001.png)
 * Applies company-branded background color when configured for new-style avatars
 */
const AvatarDisplay = ({
  src,
  alt = 'Avatar',
  size = 'md',
  shape = 'circle',
  className = '',
  companySettings = {},
  showInitials = true,
  initialsName = '',
}) => {
  const [imageError, setImageError] = useState(false);

  // Size classes mapping
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-20 h-20 text-xl',
    xl: 'w-24 h-24 text-2xl',
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Get avatar URL and background color in one call
  const avatar = getAvatarStyle(src, companySettings);
  const initials = getInitials(initialsName || alt);
  const shapeClass = {
    circle: 'rounded-full',
    rounded: 'rounded-lg',
    rectangle: 'rounded-none',
  }[shape] || 'rounded-full';

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div
      className={`${shapeClass} overflow-hidden flex items-center justify-center ${sizeClasses[size] || sizeClasses.md} ${className}`}
      style={{ backgroundColor: avatar.backgroundColor }}
    >
      {avatar.url && !imageError ? (
        <img
          src={avatar.url}
          alt={alt}
          className={`w-full h-full ${avatar.isNewStyle ? 'object-contain' : 'object-cover'}`}
          style={avatar.isNewStyle ? { transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
          onError={handleImageError}
        />
      ) : showInitials ? (
        <span className="text-white font-bold">{initials}</span>
      ) : null}
    </div>
  );
};

AvatarDisplay.propTypes = {
  /** Avatar filename (e.g., 'avatar_001.png' or 'rj-1-rectangle.png') */
  src: PropTypes.string,
  /** Alt text for the image */
  alt: PropTypes.string,
  /** Size variant: 'xs', 'sm', 'md', 'lg', 'xl' */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /** Shape variant: circle, rounded rectangle, or hard rectangle */
  shape: PropTypes.oneOf(['circle', 'rounded', 'rectangle']),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Company settings object with avatar configuration */
  companySettings: PropTypes.shape({
    avatar_directory: PropTypes.string,
    avatar_background_color: PropTypes.string,
    primary_color: PropTypes.string,
  }),
  /** Whether to show initials when image fails to load */
  showInitials: PropTypes.bool,
  /** Name to generate initials from (falls back to alt) */
  initialsName: PropTypes.string,
};

export default AvatarDisplay;
